import type { AsyncSelectProps } from "@/components/ui/FormFields/AsyncSelectOptions";
import { SearchableSelectFormField } from "@/components/ui/FormFields/SearchableSelectFormField/SearchableSelectFormField";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { XIcon } from "lucide-react";
import { useId, useRef } from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";

interface SelectFormFieldProps<T> extends AsyncSelectProps {
  className?: string;
  optionsClassName?: string;
  emptyMessage?: string;
  isItemsError?: boolean;
  isItemsLoading?: boolean;
  items: T[];
  itemsKey: keyof T;
  formatSelectedItemLabel?: (item?: T) => string | undefined;
  formatItemContext?: (item?: T) => string | undefined;
  formatItemContextClassName?: (item?: T) => string | undefined;
  formatItemLabel?: (item?: T) => string | undefined;
  formatItemSuffix?: (item?: T) => string | undefined;
  label?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  ref?: React.RefObject<HTMLDivElement>;
  register: UseFormRegisterReturn;
  clearable?: boolean;
}

export const SelectFormField = <T,>({
  className,
  optionsClassName,
  emptyMessage,
  isItemsError,
  isItemsLoading,
  items,
  itemsKey,
  formatSelectedItemLabel,
  formatItemLabel,
  formatItemContext,
  formatItemContextClassName,
  formatItemSuffix,
  label,
  onChange,
  placeholder,
  ref,
  register,
  clearable = false,
  ...asyncProps
}: SelectFormFieldProps<T>) => {
  const uid = useId();
  const triggerId = `select-${register.name}-${uid}`;

  const form = useFormContext();

  // This is needed to allow the select to be disabled when it is inside a disabled fieldset
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isInDisabledFieldset = Boolean(
    triggerRef.current?.closest("fieldset:disabled"),
  );

  if (asyncProps.onSearchChange) {
    return (
      <SearchableSelectFormField
        {...asyncProps}
        register={register}
        label={label}
        items={items}
        itemsKey={itemsKey}
        buttonClassName={className}
        optionsClassName={optionsClassName}
        emptyMessage={emptyMessage}
        isItemsError={isItemsError}
        isItemsLoading={isItemsLoading}
        formatSelectedItemLabel={formatSelectedItemLabel}
        formatItemLabel={formatItemLabel}
        formatItemContext={formatItemContext}
        formatItemSuffix={formatItemSuffix}
        onChange={onChange}
        placeholder={placeholder}
        clearable={clearable}
      />
    );
  }

  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => (
        <FormItem>
          {label && (
            <div className="flex">
              <FormLabel htmlFor={triggerId}>{label}</FormLabel>
              <FormMessage
                data-testid="error-message"
                className="grow text-right"
              />
            </div>
          )}

          <Select
            {...field}
            // @ts-expect-error This line is to remove a console error
            ref={null}
            disabled={isInDisabledFieldset}
            value={field.value ?? ""}
            onValueChange={(value) => {
              onChange?.(value);
              field.onChange(value, {
                shouldDirty: true,
              });
            }}
          >
            <FormControl>
              <div className="relative">
                <SelectTrigger
                  id={triggerId}
                  ref={triggerRef}
                  disabled={isInDisabledFieldset}
                  className={cn(
                    className,
                    !field.value && "text-muted-foreground",
                  )}
                  // Helpful fallback when label is omitted
                  aria-label={!label ? (placeholder ?? field.name) : undefined}
                >
                  <SelectValue placeholder={placeholder ?? ""}>
                    {field.value
                      ? ((formatSelectedItemLabel ?? formatItemLabel)?.(
                          items.find((item) => item[itemsKey] === field.value),
                        ) ?? field.value)
                      : undefined}
                  </SelectValue>
                </SelectTrigger>

                {clearable && field.value && (
                  <button
                    type="button"
                    aria-label={label ? `Clear ${label}` : "Clear selection"}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-8 -translate-y-1/2"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange?.("");
                      form.setValue(register.name, "", {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </FormControl>

            <SelectContent ref={ref}>
              {items.map((item) => {
                const suffix = formatItemSuffix?.(item);
                return (
                  <SelectItem
                    key={item[itemsKey] as string}
                    value={item[itemsKey] as string}
                  >
                    <Typography
                      className={cn(
                        "line-clamp-2 max-w-[500px]",
                        optionsClassName,
                      )}
                    >
                      {formatItemLabel?.(item)}
                      {suffix && (
                        <span className="text-muted-foreground">{` ${suffix}`}</span>
                      )}
                    </Typography>
                    <Typography
                      variant="subHeading"
                      className={cn(
                        "line-clamp-3 max-w-[500px]",
                        formatItemContextClassName?.(item),
                      )}
                    >
                      {formatItemContext && formatItemContext(item)}
                    </Typography>
                  </SelectItem>
                );
              })}

              {isItemsLoading && (
                <SelectItem
                  value="null"
                  disabled
                  className="flex justify-center"
                >
                  <LoadingSpinner />
                </SelectItem>
              )}

              {isItemsError && (
                <SelectItem value="null" disabled>
                  Unable to load items.
                </SelectItem>
              )}

              {!isItemsLoading && !isItemsError && items.length === 0 && (
                <SelectItem value="null" disabled>
                  {emptyMessage ?? "No items available"}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </FormItem>
      )}
    />
  );
};

SelectFormField.displayName = "SelectFormField";
